import { kebabCase } from 'lodash';
import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CompanyId, UserType } from 'api/models';
import { PageLoading } from 'components/atoms/loading';
import { useCurrentUserSideEffects } from 'contexts/providers';
import { RootState } from 'data';
import { saveFields } from 'data/fields';
import { useAccount } from 'hooks/use-account';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import {
  addProjectsToIndexDb,
  usePrerenderMembersAndTeamsFromIndexedDb,
} from 'hooks/use-idb';
import { useUser } from 'hooks/use-user';
import {
  useAllFieldsQuery,
  useProjectsQuery,
  useTaskFieldsQuery,
} from 'queries';
import { useAccountDataQuery } from 'queries/account';
import { useMembersV2Query } from 'queries/members';
import { putAll, refresh } from 'stores/fields';
import { putAll as putAllProjects } from 'stores/projects';
import { getRGBFromHex } from 'utils/get-rgb-from-hex';

const LoadDependents = ({ onComplete }) => {
  useCurrentUserSideEffects();
  const dispatch = useDispatch();
  const companiesStore = useSelector(({ companies }: RootState) => companies);
  const usersStore = useSelector(({ users }: RootState) => users);
  const teamsStore = useSelector(({ teams }: RootState) => teams);
  const projectsStore = useSelector(
    ({ projects_v2 }: RootState) => projects_v2,
  );
  const membershipsStore = useSelector(
    ({ memberships }: RootState) => memberships,
  );
  const [fieldsLoaded, setFieldsLoaded] = useState(false);

  useAllFieldsQuery({
    onSuccess: (fields) => {
      dispatch(putAll(fields));
      setFieldsLoaded(true); // this is enough as dispacth is synchronous
    },
  });

  const { user: currentUser } = useUser();
  const {
    company: { companyId },
    userType,
  } = currentUser;
  const { systemBotId } = useAccount();

  useEffect(() => {
    if (!fieldsLoaded) {
      return;
    }
    dispatch(
      refresh([
        usersStore,
        teamsStore,
        projectsStore,
        membershipsStore,
        companiesStore,
        CompanyId(companyId),
        currentUser,
        currentUser.userType === UserType.Native ? systemBotId : undefined,
      ]),
    );
  }, [
    companiesStore,
    companyId,
    currentUser,
    dispatch,
    fieldsLoaded,
    membershipsStore,
    projectsStore,
    systemBotId,
    teamsStore,
    usersStore,
  ]);

  useEffect(() => {
    if (userType === UserType.External) {
      window.setTimeout(() => onComplete());
    }
  }, [onComplete, userType]);

  return null;
};

interface Props {
  children: any;
  isAccountDataFetched: boolean;
  onDependentsLoaded: (loading: boolean) => void;
  isLoading: boolean;
}

const RenderBootApp: FC<Props> = ({
  children,
  isAccountDataFetched,
  onDependentsLoaded,
}) => {
  return isAccountDataFetched ? (
    <>
      <LoadDependents onComplete={onDependentsLoaded} />
      {children}
    </>
  ) : (
    <PageLoading css />
  );
};

const stopRerenderPostLoading = (
  pre: Readonly<React.PropsWithChildren<Props>>,
): boolean => !!pre.isLoading;
const MuteBootUpdatesPostLoading = memo(RenderBootApp, stopRerenderPostLoading);

export const LazyBootApp = ({ children }) => {
  const [isLoading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const [dependentsLoaded, setDependentsLoaded] = useState(false);

  const { isSuccess: isAccountDataFetched, data: accountData } =
    useAccountDataQuery();

  const { isSuccess: isTaskFieldsFetched } = useTaskFieldsQuery({
    onSuccess: (result) => {
      dispatch(saveFields(result));
    },
  });
  const [membersDataLoadedIntoIndexedDB, setMembersDataLoadedIntoIndexedDB] =
    useState(false);

  const { isSuccess: isMembersV2Fetched } = useMembersV2Query({}, () => {
    setMembersDataLoadedIntoIndexedDB(true);
  });
  const { isSuccess: isProjectsFetched } = useProjectsQuery({
    onSuccess: (projects) => {
      dispatch(putAllProjects(projects));
    },
  });

  usePrerenderMembersAndTeamsFromIndexedDb();

  useEffect(() => {
    if (
      isAccountDataFetched &&
      isTaskFieldsFetched &&
      isProjectsFetched &&
      dependentsLoaded &&
      isMembersV2Fetched &&
      membersDataLoadedIntoIndexedDB
    ) {
      requestAnimationFrame(() => setLoading(true));
    }
  }, [
    isAccountDataFetched,
    isTaskFieldsFetched,
    isProjectsFetched,
    dependentsLoaded,
    isMembersV2Fetched,
    membersDataLoadedIntoIndexedDB,
  ]);

  const onDependentsLoaded = useCallback(() => {
    setDependentsLoaded(true);
  }, []);
  const [, setTimesheetsFeature] = useAccountFeature(AccountFeature.Timesheets);
  const [, setFinancialsFeature] = useAccountFeature(AccountFeature.Financials);
  const [, setAccountabilityFeature] = useAccountFeature(
    AccountFeature.Accountability,
  );

  useLayoutEffect(() => {
    if (accountData) {
      const {
        account: { metaData },
        accountMeta: { features },
        userProfile: { projects },
      } = accountData;

      if (metaData) {
        const { theme } = metaData;

        if (theme) {
          const root = document.documentElement;

          Object.keys(theme).forEach((variable) => {
            const variableValue = theme[variable];

            if (variableValue) {
              root.style.setProperty(`--${kebabCase(variable)}`, variableValue);
            }
          });
          if (theme.brandColorInRgb == null) {
            const themeBrandInNum = theme?.brandColor.slice(1);

            root.style.setProperty(
              `--${kebabCase('brandColorInRgb')}`,
              `${getRGBFromHex(themeBrandInNum)[0]}, ${
                getRGBFromHex(themeBrandInNum)[1]
              }, ${getRGBFromHex(themeBrandInNum)[2]}`,
            );
          }
        }
      }

      if (projects.length) {
        addProjectsToIndexDb(projects);
      }
      if (features?.[AccountFeature.Timesheets] != null) {
        setTimesheetsFeature(features[AccountFeature.Timesheets]);
      }

      if (features?.[AccountFeature.Financials] != null) {
        setFinancialsFeature(features[AccountFeature.Financials]);
      }

      // For a temp period, this flag will be present in API; once feature is live, flag will be removed
      setAccountabilityFeature(
        features?.[AccountFeature.Accountability] ?? true,
      );
    }
  }, [
    accountData,
    setAccountabilityFeature,
    setFinancialsFeature,
    setTimesheetsFeature,
  ]);

  // TODO: Remove this CurrentUserProvider once we have a better way to handle this
  // load routes only when account data is fetched and indexdb is populated
  return (
    <MuteBootUpdatesPostLoading
      isAccountDataFetched={
        !!(isAccountDataFetched && membersDataLoadedIntoIndexedDB)
      }
      onDependentsLoaded={onDependentsLoaded}
      isLoading={isLoading}
    >
      <>{children}</>
    </MuteBootUpdatesPostLoading>
  );
};
